import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Formik, Form } from 'formik';
import moment from 'moment';
import get from 'lodash/get';

import Group from '@lightspeed/cirrus-group';
import Text from '@lightspeed/cirrus-text';
import { Divider } from '@lightspeed/flame/Divider';
import Input from '@lightspeed/cirrus-input';
import { Button } from '@lightspeed/flame/Button';
import { Alert } from '@lightspeed/flame/Alert';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';

import { START_DATE, END_DATE } from '.';
import { validate } from './utils';

const DateRangePickerForm = ({
  single,
  startDate,
  endDate,
  focusedInput,
  dateFormat,
  availableStartDate,
  availableEndDate,
  numberOfMonths,
  hasPresets,
  offset,
  customValidation,
  onStartDateFocus,
  onStartDateBlur,
  onEndDateFocus,
  onEndDateBlur,
  onSubmit,
}) => (
  <Formik
    enableReinitialize
    initialValues={{
      [`${START_DATE}`]: moment(startDate).format(dateFormat),
      [`${END_DATE}`]: moment(endDate).format(dateFormat),
    }}
    validateOnChange={false}
    validate={validate({
      availableStartDate,
      availableEndDate,
      dateFormat,
      single,
      offset,
      customValidation,
    })}
    onSubmit={onSubmit}
    render={({ values, errors, setFieldValue, handleChange, handleBlur }) => {
      const startDateCls = cn({ 'input-focus': focusedInput === START_DATE });
      const endDateCls = cn({
        'input-focus': focusedInput === END_DATE,
        'input-dimmed':
          startDate.isSame(endDate, 'day') && focusedInput !== END_DATE && !errors[END_DATE],
      });

      const formatDateField = field => {
        const formattedValue = moment(values[field], dateFormat);
        if (formattedValue.isValid()) {
          setFieldValue(field, formattedValue.format(dateFormat));
        }
      };

      const handleStartDateBlur = event => {
        formatDateField(START_DATE);
        handleBlur(event);
        onStartDateBlur(event);
      };

      const handleEndDateBlur = event => {
        formatDateField(END_DATE);
        handleBlur(event);
        onEndDateBlur(event);
      };

      // In case of a single month without presets the error message is longer than the calendar
      const alertCls = cn('cr-date-picker__alert', 'cr-text-left', {
        'cr-date-picker__alert--fixed-width': numberOfMonths === 1 && !hasPresets,
      });

      const isErrorUnique = ({ startDateErrors, endDateErrors }) =>
        get(startDateErrors, 'props.children', '') !== get(endDateErrors, 'props.children', '');

      return (
        <Form autoComplete="off">
          <div className="cr-date-picker__form">
            <div className="cr-p-2">
              <Group>
                <div className={startDateCls}>
                  <Input
                    style={{ width: '105px' }}
                    name={START_DATE}
                    onFocus={onStartDateFocus}
                    onBlur={handleStartDateBlur}
                    onChange={handleChange}
                    value={values[START_DATE]}
                    placeholder={dateFormat}
                    autoComplete="off"
                    status={{
                      type: errors[START_DATE] ? 'error' : undefined,
                    }}
                    data-test="date-picker-start-date-input"
                  />
                </div>
                {!single && <IconArrowRight />}
                {!single && (
                  <div className={endDateCls}>
                    <Input
                      style={{ width: '105px' }}
                      name={END_DATE}
                      onFocus={onEndDateFocus}
                      onBlur={handleEndDateBlur}
                      onChange={handleChange}
                      value={values[END_DATE]}
                      placeholder={dateFormat}
                      autoComplete="off"
                      status={{
                        type: errors[END_DATE] ? 'error' : undefined,
                      }}
                      data-test="date-picker-end-date-input"
                    />
                  </div>
                )}
                <Button type="submit" variant="primary" fill data-test="date-picker-submit-input">
                  Apply
                </Button>
              </Group>
            </div>
            {(errors[START_DATE] || errors[END_DATE]) && (
              <Alert type="danger" className={alertCls} noCloseBtn>
                <Fragment>
                  {errors[START_DATE] && <Text>{errors[START_DATE]}</Text>}
                  {errors[END_DATE] &&
                    isErrorUnique({
                      startDateErrors: errors[START_DATE],
                      endDateErrors: errors[END_DATE],
                    }) && <Text>{errors[END_DATE]}</Text>}
                </Fragment>
              </Alert>
            )}
            <Divider />
          </div>
        </Form>
      );
    }}
  />
);

DateRangePickerForm.propTypes = {
  single: PropTypes.bool,
  startDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }).isRequired,
  endDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }).isRequired,
  focusedInput: PropTypes.oneOf(['startDate', 'endDate']),
  dateFormat: PropTypes.string,
  availableStartDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }),
  availableEndDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }),
  hasPresets: PropTypes.bool,
  offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customValidation: PropTypes.func,
  onStartDateFocus: PropTypes.func.isRequired,
  onStartDateBlur: PropTypes.func.isRequired,
  onEndDateFocus: PropTypes.func.isRequired,
  onEndDateBlur: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

DateRangePickerForm.defaultProps = {
  focusedInput: 'startDate',
  single: false,
};

export default DateRangePickerForm;
